/**
 * SaveAndStartFreshModal — confirmation dialog for starting a brand-new draft
 * while the current Word document already holds a run.
 *
 * The add-in binds one document to one run, so a fresh draft can't quietly
 * overlay an existing project. This modal makes the trade explicit: confirming
 * saves the current document, then rotates to a fresh run so the new
 * submission's section picker can appear. Modelled on KillRunConfirmation's
 * controlled open/confirming/Escape/backdrop pattern.
 */

import type React from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

interface SaveAndStartFreshModalProps {
  /** When true, render the dialog. */
  open: boolean;
  /** Handler when the writer confirms — save then start fresh. */
  onConfirm: () => void | Promise<void>;
  /** Handler when the writer cancels — keep the current project. */
  onCancel: () => void;
  /** When true, disable buttons and show the saving state. */
  confirming?: boolean;
}

export const SaveAndStartFreshModal: React.FC<SaveAndStartFreshModalProps> = ({
  open,
  onConfirm,
  onCancel,
  confirming = false,
}) => {
  if (!open) return null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key === "Escape" && !confirming) {
      onCancel();
    }
  };

  return (
    <dialog
      onClick={confirming ? undefined : onCancel}
      onKeyDown={handleKeyDown}
      open
      style={{
        position: "fixed",
        inset: "0",
        background: "rgba(0, 0, 0, 0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
        border: "none",
        margin: "auto",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="document"
        style={{
          background: surfaceColors.paper,
          borderRadius: "8px",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.16)",
          padding: "24px",
          maxWidth: "480px",
          width: "100%",
          fontFamily: fonts.body,
        }}
      >
        <div
          style={{
            fontSize: "18px",
            fontWeight: 700,
            color: inkColors.ink,
            marginBottom: "12px",
            lineHeight: "1.4",
          }}
        >
          Start a new draft?
        </div>
        <div
          style={{
            fontSize: "13px",
            fontWeight: 400,
            color: inkColors.ink2,
            marginBottom: "16px",
            lineHeight: "1.5",
          }}
        >
          This document already has a project. Continuing will save the current document, then start
          a fresh draft for the new submission — you'll pick its sections next.
        </div>
        <div
          style={{
            fontSize: "12px",
            fontWeight: 400,
            color: inkColors.ink4,
            background: "#F5F5F5",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
            lineHeight: "1.5",
            borderLeft: `3px solid ${statusColors.warn}`,
          }}
        >
          Your current work is saved to its own project first, so nothing is lost — you can reopen it
          from Drafts later.
        </div>
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={confirming}
            style={{
              fontFamily: fonts.body,
              fontSize: "12px",
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: "4px",
              border: `1px solid ${surfaceColors.line}`,
              background: "transparent",
              color: inkColors.ink2,
              cursor: confirming ? "default" : "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              transition: "all 150ms",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              opacity: confirming ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming}
            style={{
              fontFamily: fonts.body,
              fontSize: "12px",
              fontWeight: 600,
              padding: "8px 16px",
              borderRadius: "4px",
              border: "none",
              background: brandColors.primary,
              color: "#fff",
              cursor: confirming ? "default" : "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              transition: "all 150ms",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              opacity: confirming ? 0.5 : 1,
            }}
          >
            {confirming ? "Saving…" : "Save & start fresh"}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default SaveAndStartFreshModal;
